import { useEffect, useState } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Copy, Link2, Crown, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  owner: { label: "Mister", color: "bg-amber-100 text-amber-700", icon: Crown },
  director: { label: "Dirigente", color: "bg-sky-100 text-sky-700", icon: ShieldCheck },
  parent: { label: "Genitore", color: "bg-emerald-100 text-emerald-700", icon: User },
};

export default function MembersPage() {
  const { activeTeam, refresh } = useTeams();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("director");
  const [playerId, setPlayerId] = useState("");
  const [email, setEmail] = useState("");
  const [created, setCreated] = useState(null);

  const isOwner = activeTeam?.my_role === "owner";
  const canManage = isOwner || activeTeam?.my_role === "director";

  const load = async () => {
    if (!activeTeam) return;
    const [m, i, p] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/members`),
      api.get(`/teams/${activeTeam.id}/invites`),
      api.get(`/teams/${activeTeam.id}/players`),
    ]);
    setMembers(m.data);
    setInvites(i.data);
    setPlayers(p.data);
  };

  useEffect(() => {
    load();
  }, [activeTeam]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submitInvite = async () => {
    if (role === "parent" && !playerId) {
      toast.error("Seleziona il giocatore per il genitore");
      return;
    }
    try {
      const { data } = await api.post(`/teams/${activeTeam.id}/invites`, {
        role,
        player_id: role === "parent" ? playerId : null,
        email: email || null,
      });
      setCreated(data);
      setEmail("");
      setPlayerId("");
      load();
      toast.success("Invito creato");
    } catch (e) {
      toast.error("Impossibile creare l'invito");
    }
  };

  const inviteUrl = (token) =>
    `${window.location.origin}/invito/${token}`;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copiato!");
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Rimuovere ${m.name || m.email}?`)) return;
    await api.delete(`/teams/${activeTeam.id}/members/${m.id}`);
    toast.success("Membro rimosso");
    load();
    refresh();
  };

  const revokeInvite = async (i) => {
    if (!window.confirm("Revocare l'invito?")) return;
    await api.delete(`/teams/${activeTeam.id}/invites/${i.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Gestione utenti</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Staff & genitori</h1>
          <p className="text-slate-500">{members.length} membri attivi</p>
        </div>
        {canManage && (
          <Button
            onClick={() => {
              setCreated(null);
              setOpen(true);
            }}
            className="rounded-full bg-sky-500 hover:bg-sky-600"
            data-testid="new-invite-btn"
          >
            <Plus size={16} className="mr-1" /> Invita
          </Button>
        )}
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <h2 className="font-heading text-lg font-semibold">Membri</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {members.map((m) => {
            const badge = ROLE_BADGE[m.role] || ROLE_BADGE.director;
            const Icon = badge.icon;
            const player = m.player_id ? players.find((p) => p.id === m.player_id) : null;
            return (
              <div
                key={m.id}
                className="p-5 flex items-center gap-4"
                data-testid={`member-${m.id}`}
              >
                <div className="h-10 w-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold">
                  {(m.name || m.email || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{m.name || "—"}</div>
                  <div className="text-sm text-slate-500 truncate">{m.email}</div>
                  {player && (
                    <div className="text-xs text-slate-400 mt-0.5">
                      Genitore di: {player.first_name} {player.last_name}
                    </div>
                  )}
                </div>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${badge.color}`}
                >
                  <Icon size={12} /> {badge.label}
                </span>
                {isOwner && m.role !== "owner" && (
                  <Button size="icon" variant="ghost" onClick={() => removeMember(m)}>
                    <Trash2 size={15} className="text-red-500" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {canManage && invites.length > 0 && (
        <Card className="border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold">Inviti pendenti</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {invites.map((i) => {
              const badge = ROLE_BADGE[i.role] || ROLE_BADGE.director;
              return (
                <div key={i.id} className="p-5 flex items-center gap-4 flex-wrap">
                  <Link2 size={18} className="text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold mr-2 ${badge.color}`}>
                        {badge.label}
                      </span>
                      {i.email && <span className="text-slate-600">{i.email}</span>}
                    </div>
                    <div className="text-xs text-slate-400 truncate mt-1">
                      Scade: {new Date(i.expires_at).toLocaleDateString("it-IT")}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copy(inviteUrl(i.token))}
                    data-testid={`copy-invite-${i.id}`}
                  >
                    <Copy size={14} className="mr-1" /> Copia link
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => revokeInvite(i)}>
                    <Trash2 size={15} className="text-red-500" />
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{created ? "Invito creato" : "Nuovo invito"}</DialogTitle>
          <DialogDescription className="sr-only">Gestisci membri dello staff e i genitori della squadra.</DialogDescription>
          </DialogHeader>
          {!created ? (
            <div className="space-y-4">
              <div>
                <Label>Ruolo</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="director">Dirigente (può modificare)</SelectItem>
                    <SelectItem value="parent">Genitore (sola lettura)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {role === "parent" && (
                <div>
                  <Label>Giocatore</Label>
                  <Select value={playerId} onValueChange={setPlayerId}>
                    <SelectTrigger data-testid="invite-player-select">
                      <SelectValue placeholder="Seleziona giocatore" />
                    </SelectTrigger>
                    <SelectContent>
                      {players.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.last_name} {p.first_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Il genitore vedrà solo le convocazioni del proprio figlio.
                  </p>
                </div>
              )}
              <div>
                <Label>Email destinatario (opzionale)</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="genitore@email.it"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Condividi questo link con il destinatario. È valido 7 giorni.
              </p>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 break-all text-sm font-mono">
                {inviteUrl(created.token)}
              </div>
              <Button
                onClick={() => copy(inviteUrl(created.token))}
                className="w-full bg-sky-500 hover:bg-sky-600"
                data-testid="copy-invite-link-btn"
              >
                <Copy size={14} className="mr-2" /> Copia link
              </Button>
            </div>
          )}
          <DialogFooter>
            {!created ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Annulla
                </Button>
                <Button
                  onClick={submitInvite}
                  className="bg-sky-500 hover:bg-sky-600"
                  data-testid="submit-invite-btn"
                >
                  Crea invito
                </Button>
              </>
            ) : (
              <Button onClick={() => setOpen(false)}>Chiudi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
